import type { Rule } from '@/interfaces/rule'

import type { Policy } from '@/modules/groups/interfaces/policy'

/** Medianoche local de `date` más `days` días, como los días de `allowedRange`. */
const localDay = (date: Date, days = 0) =>
	new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)

/**
 * Por qué la política rígida de un grupo no deja buscar con estas fechas, o nada si sí
 * se puede. Revisa lo mismo que el calendario de AvailabilityForm:
 * - la entrada contra los días de anticipación (`daysInAdvanceRange`), de hoy + min a
 *   hoy + max en días locales, como `allowedRange`;
 * - las noches contra `nightAmount`, con el límite inclusivo, como el aviso de noches
 *   del formulario y BookingV2.
 *
 * Las flexibles no cuentan: ésas buscan y sólo avisan. Como en BookingV2, cuentan todas
 * las reglas de la política, no sólo la primera.
 *
 * Sólo recibe las políticas y el día de hoy: no depende de qué grupo esté cargado en el
 * store ni de cuándo llegó.
 */
export function strictPolicyBreach(
	policies: Policy[],
	checkIn: string,
	checkOut: string,
	today: Date = new Date()
): string | undefined {
	const strictRules = (name: string): Rule[] =>
		policies
			.filter((policy) => policy.name === name && !policy.isFlexible)
			.flatMap((policy) => policy.rule)

	const start = new Date(`${checkIn}T00:00:00`).getTime()

	const advance = strictRules('daysInAdvanceRange').find((rule) => {
		const first = localDay(today, Number(rule.min ?? 0)).getTime()
		const last =
			rule.max ? localDay(today, Number(rule.max)).getTime() : Infinity

		return start < first || start > last
	})

	if (advance) {
		const days =
			advance.max ?
				`${advance.min} a ${advance.max}`
			:	`al menos ${advance.min}`

		return `fechas con ${days} días de anticipación`
	}

	// 'YYYY-MM-DD' se lee a medianoche UTC: la resta no brinca con el horario de verano.
	const nights = Math.round(
		(Date.parse(checkOut) - Date.parse(checkIn)) / 86_400_000
	)
	const stay = strictRules('nightAmount').find(
		(rule) => nights > Number(rule.value)
	)

	if (stay) {
		const max = Number(stay.value)

		return `una estancia de máximo ${max} ${max === 1 ? 'noche' : 'noches'}`
	}

	return undefined
}
